import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { WHATSAPP_NUMBER, WHATSAPP_DISPLAY, SHOP_PHONE_TEL, SHOP_PHONE_DISPLAY } from '../config/siteConfig'
import { trackPixelContact } from '../utils/metaPixel'
import { logContactClick } from '../firebase'

const MAPS_EMBED_SRC = 'https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d905.3627903863259!2d67.0364315!3d24.8142403!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3eb33d701c1a87d5%3A0x162d8e7949706760!2sMohammad%20Akhtar%20and%20Sabir%20wholesale%20vegetable%20center!5e0!3m2!1sen!2s!4v1783449328355!5m2!1sen!2s'

// The iframe's own `loading="lazy"` wasn't enough on its own - Lighthouse
// still counted ~400 KiB of Google Maps JS against every page, because the
// footer (and this map) sits close enough to the fold on some pages that
// the browser's native lazy-load distance still triggers it. This
// IntersectionObserver only sets the iframe's src once the map's own
// wrapper is actually about to scroll into view, so a visitor who never
// reaches the footer never loads Maps at all, on any page.
const MapEmbed = () => {
  const wrapperRef = useRef(null)
  const [shouldLoad, setShouldLoad] = useState(false)

  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px 0px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={wrapperRef} style={{ height: 300, borderRadius: '12px', overflow: 'hidden' }}>
      {shouldLoad ? (
        <iframe
          title="Google Maps Location"
          src={MAPS_EMBED_SRC}
          width="100%"
          height="300"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
        ></iframe>
      ) : (
        <div className="w-full h-full bg-white/5 animate-pulse" aria-hidden="true" />
      )}
    </div>
  )
}

const Footer = () => {
  const location = useLocation()
  return (
    <footer className="bg-charcoal text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div className="md:col-span-2">
            <h3 className="text-lg font-semibold mb-4">Visit Our Shop</h3>
            <MapEmbed />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          <div>
            <div className="inline-block bg-white rounded-xl px-4 py-2 mb-4">
              <img src="/brand-logo.webp" alt="Akhtar Sabir Fresh - fresh meat and vegetable shop in Karachi" width={87} height={48} className="h-12 w-auto" />
            </div>
            <p className="text-gray-400">Fresh Meat & Vegetables Prepared After Your Order</p>
          </div>

          {/* Sitewide links to the local landing pages. These sit in the
              footer on purpose: it puts a crawlable path to every one of
              them on every page of the site, which is how Googlebot finds
              and re-crawls them without depending on the sitemap alone. The
              anchor text is the search phrase itself rather than "click
              here", because anchor text is a ranking signal for the target
              page. */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Shop in Karachi</h3>
            <ul className="space-y-2">
              <li><Link to="/fresh-vegetables-karachi" className="text-gray-400 hover:text-fresh-green transition-colors">Fresh Vegetables in Karachi</Link></li>
              <li><Link to="/fresh-fruits-karachi" className="text-gray-400 hover:text-fresh-green transition-colors">Fresh Fruits in Karachi</Link></li>
              <li><Link to="/fresh-chicken-karachi" className="text-gray-400 hover:text-fresh-green transition-colors">Fresh Chicken in Karachi</Link></li>
              <li><Link to="/fresh-beef-karachi" className="text-gray-400 hover:text-fresh-green transition-colors">Fresh Beef in Karachi</Link></li>
              <li><Link to="/fresh-meat-karachi" className="text-gray-400 hover:text-fresh-green transition-colors">Fresh Meat in Karachi</Link></li>
              <li><Link to="/grocery-delivery-karachi" className="text-gray-400 hover:text-fresh-green transition-colors">Grocery Delivery in Karachi</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="text-gray-400 hover:text-fresh-green transition-colors">Home</Link></li>
              <li><Link to="/shop" className="text-gray-400 hover:text-fresh-green transition-colors">Shop</Link></li>
              <li><Link to="/blog" className="text-gray-400 hover:text-fresh-green transition-colors">Fresh Food Guides</Link></li>
              <li><Link to="/about" className="text-gray-400 hover:text-fresh-green transition-colors">About Us</Link></li>
              <li><Link to="/contact" className="text-gray-400 hover:text-fresh-green transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Customer Service</h3>
            <ul className="space-y-2">
              <li><Link to="/delivery" className="text-gray-400 hover:text-fresh-green transition-colors">Delivery Info</Link></li>
              <li><Link to="/preorder" className="text-gray-400 hover:text-fresh-green transition-colors">How It Works</Link></li>
              <li><Link to="/terms-of-service" className="text-gray-400 hover:text-fresh-green transition-colors">Terms of Service</Link></li>
              <li><Link to="/refund-policy" className="text-gray-400 hover:text-fresh-green transition-colors">Refund Policy</Link></li>
              <li><Link to="/privacy-policy" className="text-gray-400 hover:text-fresh-green transition-colors">Privacy Policy</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-2 text-gray-400">
              {/* Real tel:/wa.me links, not plain text - a mobile audit of
                  the buy funnel found these were unclickable spans, which
                  meant a customer had to manually select and copy a phone
                  number to call or message it. One tap now dials or opens
                  WhatsApp directly, matching WhatsAppButton.jsx's number.
                  Both also fire the same click-tracking as that button, so
                  a contact click from the footer counts the same as one
                  from the floating button - see attribution.js for why the
                  click is as far as this tracking can ever see. */}
              <li className="flex items-center space-x-2">
                <span>📞</span>
                <a
                  href={`tel:${SHOP_PHONE_TEL}`}
                  onClick={() => { trackPixelContact('phone'); logContactClick('phone', location.pathname) }}
                  className="hover:text-fresh-green transition-colors"
                >{SHOP_PHONE_DISPLAY}</a>
              </li>
              <li className="flex items-center space-x-2">
                <span>📱</span>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => { trackPixelContact('whatsapp'); logContactClick('whatsapp', location.pathname) }}
                  className="hover:text-fresh-green transition-colors"
                >WhatsApp: {WHATSAPP_DISPLAY}</a>
              </li>
              <li className="flex items-center space-x-2">
                <span>📍</span>
                <span>R27P+WJX, Khayaban-e-Shujaat, Shah Rasool Colony Neelam Colony, Karachi, 75500, Pakistan</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
          <p>&copy; 2026 Akhtar Sabir Fresh. All rights reserved.</p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
